import React, { useState } from 'react';
import { Youtube, ArrowRight, Check, Zap, Star, LayoutDashboard } from 'lucide-react';

interface LandingPageProps {
    onLogin: () => void;
    onSignup: (intent?: 'free' | 'pro_monthly' | 'pro_annual') => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onLogin, onSignup }) => {
    const [isAnnual, setIsAnnual] = useState(true);

    return (
        <div className="min-h-screen bg-[#F8F9FB] text-slate-900 font-sans selection:bg-blue-100">
            {/* Navbar */}
            <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md border-b border-slate-100 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center text-white">
                                <Youtube size={20} />
                            </div>
                            <span className="font-bold text-xl tracking-tight">TubeAI</span>
                        </div>
                        <div className="flex gap-4">
                            <button
                                onClick={onLogin}
                                className="px-6 py-2.5 text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors"
                            >
                                Entrar
                            </button>
                            <button
                                onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                                className="px-6 py-2.5 text-sm font-bold bg-slate-900 text-white rounded-full hover:bg-slate-800 transition-all shadow-lg shadow-slate-900/20"
                            >
                                Assinar
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-40 pb-20 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-600 font-medium text-sm mb-8 border border-blue-100">
                        <Zap size={16} />
                        Nova IA de Análise de Vídeos
                    </div>
                    <h1 className="text-5xl md:text-7xl font-extrabold text-slate-900 mb-8 tracking-tight leading-[1.1]">
                        Transforme horas de vídeo em <br className="hidden md:block" />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                            Estratégia de Conteúdo
                        </span>
                    </h1>
                    <p className="text-xl text-slate-500 mb-12 max-w-2xl mx-auto leading-relaxed">
                        Cole a transcrição do seu vídeo favorito. Nossa IA extrai os melhores insights, cria resumos práticos e escreve seu conteúdo.
                    </p>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <button
                            onClick={() => onSignup('free')}
                            className="px-8 py-4 bg-slate-900 text-white rounded-full font-bold text-lg hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/20 hover:scale-105 flex items-center justify-center gap-2"
                        >
                            Começar Gratuitamente
                            <ArrowRight size={20} />
                        </button>
                        <button
                            onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                            className="px-8 py-4 bg-white text-slate-900 rounded-full font-bold text-lg hover:bg-slate-50 border border-slate-200 transition-all flex items-center justify-center gap-2"
                        >
                            Ver Planos
                        </button>
                    </div>

                    {/* Social Proof */}
                    <div className="mt-16 pt-8 border-t border-slate-200">
                        <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-6">Confiado por grandes criadores</p>
                        <div className="flex justify-center flex-wrap gap-8 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
                            {/* Replace with actual logos if you have them */}
                            <div className="text-xl font-bold font-serif text-slate-800">Assimetria Digital</div>
                            <div className="text-xl font-bold font-serif text-slate-800">Vibe Coding</div>
                            <div className="text-xl font-bold font-serif text-slate-800">Creator Studio</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-24 bg-white border-t border-slate-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center max-w-2xl mx-auto mb-16">
                        <h2 className="text-4xl font-bold text-slate-900 mb-4 tracking-tight">Planos Simples e Transparentes</h2>
                        <p className="text-lg text-slate-500 mb-8">Escolha o plano perfeito para acelerar sua criação de conteúdo.</p>

                        {/* Billing Toggle */}
                        <div className="flex items-center justify-center gap-4 mb-12">
                            <span className={`text-sm font-bold ${!isAnnual ? 'text-slate-900' : 'text-slate-500'}`}>Mensal</span>
                            <button
                                onClick={() => setIsAnnual(!isAnnual)}
                                className="w-16 h-8 bg-blue-600 rounded-full flex items-center p-1 transition-all"
                            >
                                <div className={`w-6 h-6 bg-white rounded-full shadow-sm transform transition-transform ${isAnnual ? 'translate-x-8' : ''}`} />
                            </button>
                            <span className={`text-sm font-bold flex items-center gap-2 ${isAnnual ? 'text-slate-900' : 'text-slate-500'}`}>
                                Anual <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">-20%</span>
                            </span>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {/* Free Plan */}
                        <div className="bg-[#F8F9FB] rounded-[32px] p-8 md:p-12 border border-slate-100 relative">
                            <h3 className="text-2xl font-bold text-slate-900 mb-2">Free</h3>
                            <p className="text-slate-500 mb-6">Para testar a plataforma.</p>
                            <div className="mb-8">
                                <span className="text-5xl font-extrabold text-slate-900">R$ 0</span>
                                <span className="text-slate-500 font-medium"> /mês</span>
                            </div>
                            <ul className="space-y-4 mb-8">
                                <li className="flex items-center gap-3 text-slate-700">
                                    <Check size={20} className="text-blue-500" />
                                    <span>Até 3 projetos por mês</span>
                                </li>
                                <li className="flex items-center gap-3 text-slate-700">
                                    <Check size={20} className="text-blue-500" />
                                    <span>5 projetos favoritos</span>
                                </li>
                                <li className="flex items-center gap-3 text-slate-700">
                                    <Check size={20} className="text-blue-500" />
                                    <span>Retenção de histórico por 7 dias</span>
                                </li>
                                <li className="flex items-center gap-3 text-slate-400 line-through">
                                    <Check size={20} className="opacity-50" />
                                    <span>Exportação em PDF</span>
                                </li>
                            </ul>
                            <button
                                onClick={() => onSignup('free')}
                                className="w-full py-4 bg-white text-slate-900 border-2 border-slate-200 rounded-xl font-bold hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center gap-2"
                            >
                                Começar Grátis
                            </button>
                        </div>

                        {/* Pro Plan */}
                        <div className="bg-slate-900 rounded-[32px] p-8 md:p-12 border-2 border-blue-500 relative transform md:-translate-y-4 shadow-2xl shadow-blue-900/20">
                            <div className="absolute top-0 right-8 transform -translate-y-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-bold tracking-wide">
                                MAIS POPULAR
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-2">Pro</h3>
                            <p className="text-slate-400 mb-6">Para criadores sérios.</p>
                            <div className="mb-8 h-[60px]">
                                <span className="text-5xl font-extrabold text-white">R$ {isAnnual ? '23,17' : '29'}</span>
                                <span className="text-slate-400 font-medium"> /mês</span>
                                {isAnnual && <div className="text-sm text-blue-300 mt-1">Faturado R$ 278 anualmente</div>}
                            </div>
                            <ul className="space-y-4 mb-8">
                                <li className="flex items-center gap-3 text-white">
                                    <Check size={20} className="text-blue-400" />
                                    <span><strong>Projetos ilimitados</strong></span>
                                </li>
                                <li className="flex items-center gap-3 text-white">
                                    <Check size={20} className="text-blue-400" />
                                    <span><strong>Favoritos ilimitados</strong></span>
                                </li>
                                <li className="flex items-center gap-3 text-white">
                                    <Check size={20} className="text-blue-400" />
                                    <span><strong>Histórico permanente</strong></span>
                                </li>
                                <li className="flex items-center gap-3 text-white">
                                    <Check size={20} className="text-blue-400" />
                                    <span>Exportação em PDF</span>
                                </li>
                                <li className="flex items-center gap-3 text-white">
                                    <Check size={20} className="text-blue-400" />
                                    <span>Suporte prioritário</span>
                                </li>
                            </ul>
                            <button
                                onClick={() => onSignup(isAnnual ? 'pro_annual' : 'pro_monthly')}
                                className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 hover:scale-[1.02]"
                            >
                                Assinar PRO {isAnnual ? 'Anual' : 'Mensal'}
                                <Zap size={18} className="fill-white" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#F8F9FB] py-8 text-center border-t border-slate-100">
                <p className="text-slate-400 text-sm">© {new Date().getFullYear()} TubeAI. Todos os direitos reservados.</p>
            </footer>
        </div>
    );
};
